<script setup lang="ts">

import {mergeProps, ref} from "vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {Member, Team} from "@/types";
import {useAuth} from "@/composables/auth";
import EditInternalRoleDialog from "@/Pages/teams/components/EditInternalRoleDialog.vue";

const {user} = useAuth();

interface Props {
    team: Team
    member: Member
    items: Array<string>
}

const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'remove', userId: number): void;
}>();

const isEditInternalRoleDialogOpen = ref<boolean>(false);
const isRemoveMemberDialogOpen = ref<boolean>(false);
</script>

<template>
    <v-menu
        location="top end"
    >
        <template #activator="{ props: bind }">
            <v-tooltip
                location="top"
                content-class="bg-black border-sm text-tooltip"
                max-width="200"
            >
                <template #activator="{ props: subBind }">
                    <v-icon-btn
                        v-bind="mergeProps(bind, subBind)"
                        icon="mdi-dots-vertical"
                        size="32"
                        class="ml-2"
                        rounded="sm"
                    />
                </template>
                More actions
            </v-tooltip>
        </template>
        <v-list
            rounded="lg"
            border="sm"
            elevation="3"
            class="bg-background py-0 my-1"
            width="250"
        >
            <v-list-item
                class="text-body-2 ma-1 pl-2"
                link
                density="compact"
                @click="isEditInternalRoleDialogOpen = true"
            >
                Edit internal role
            </v-list-item>
            <v-divider />
            <v-list-item
                v-if="member.id !== props.team.user_id"
                class="text-body-2 ma-1 pl-2 text-error"
                link
                density="compact"
                @click="isRemoveMemberDialogOpen = true"
            >
                {{ user.id !== member.id ? 'Remove member' : 'Leave resource' }}
            </v-list-item>
        </v-list>
    </v-menu>
    <delete-dialog
        v-model="isRemoveMemberDialogOpen"
        @submit="emit('remove', member.id)"
    >
        <template #title>
            Remove member
        </template>
        <template #default>
            Are you sure you want to {{ member.name }} from "{{ props.team.name }}"?
        </template>
        <template #label>
            Remove member
        </template>
    </delete-dialog>
    <edit-internal-role-dialog
        v-model="isEditInternalRoleDialogOpen"
        :team-id="props.team.id"
        :user="props.member"
        :items="props.items"
    />
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 42px;
}

.v-list-item--density-compact.v-list-item--one-line {
    min-height: 32px;
}
</style>
